// src/modules/dl/ActivationsLab.jsx
// Deep Learning → Activations
// Tabs: explorer · compare · saturation · derivatives · cheatsheet

import React, { useState } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, ReferenceLine,
} from 'recharts';
import { Panel, InfoBox, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--gold)';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'9px 18px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);

const TABS = ['explorer','compare','saturation','derivatives','cheatsheet'];

// ─── MATH ─────────────────────────────────────────────────────
const ACTS = {
  relu:       { fn: x=>Math.max(0,x),                         d: x=>x>0?1:0,                         color:P[0], formula:'max(0,x)',         range:'[0,∞)',    zeroCentered:false },
  sigmoid:    { fn: x=>1/(1+Math.exp(-x)),                    d: x=>{ const s=1/(1+Math.exp(-x)); return s*(1-s); }, color:P[2], formula:'1/(1+e⁻ˣ)',    range:'(0,1)',    zeroCentered:false },
  tanh:       { fn: x=>Math.tanh(x),                          d: x=>1-Math.tanh(x)**2,               color:P[1], formula:'(eˣ−e⁻ˣ)/(eˣ+e⁻ˣ)', range:'(-1,1)',  zeroCentered:true  },
  leaky_relu: { fn: x=>x>0?x:0.01*x,                          d: x=>x>0?1:0.01,                      color:P[3], formula:'x>0?x:0.01x',     range:'(-∞,∞)',   zeroCentered:false },
  elu:        { fn: x=>x>=0?x:0.5*(Math.exp(x)-1),            d: x=>x>=0?1:0.5*Math.exp(x),          color:P[4], formula:'x≥0?x:α(eˣ−1)',   range:'(-α,∞)',   zeroCentered:false },
  swish:      { fn: x=>x/(1+Math.exp(-x)),                    d: x=>{ const s=1/(1+Math.exp(-x)); return s+x*s*(1-s); }, color:P[5], formula:'x·σ(x)',      range:'(-∞,∞)',  zeroCentered:false },
  gelu:       { fn: x=>0.5*x*(1+Math.tanh(Math.sqrt(2/Math.PI)*(x+0.044715*x**3))), d: x=>{ const t=Math.tanh(Math.sqrt(2/Math.PI)*(x+0.044715*x**3)); const dt=1-t*t; return 0.5*(1+t)+0.5*x*dt*Math.sqrt(2/Math.PI)*(1+3*0.044715*x**2); }, color:P[6], formula:'0.5x(1+tanh(√(2/π)(x+0.044715x³)))', range:'(-∞,∞)', zeroCentered:false },
  softplus:   { fn: x=>Math.log(1+Math.exp(x)),               d: x=>1/(1+Math.exp(-x)),              color:'var(--t2)', formula:'log(1+eˣ)',   range:'(0,∞)',   zeroCentered:false },
};

const ACT_META = {
  relu:       { pros:['No vanishing gradient x>0','Sparse activations','Cheapest to compute'], cons:['Dying ReLU: x<0 → grad=0','Not zero-centered','Unbounded'],       use:'Default. Use everywhere unless reason not to.' },
  sigmoid:    { pros:['Output ∈ (0,1) — natural probability','Smooth differentiable'],        cons:['Vanishes: |x|>4 → grad≈0','Not zero-centered','Slow exp compute'],  use:'Output layer for binary classification only.' },
  tanh:       { pros:['Zero-centered output','Stronger gradient than sigmoid','Symmetric'],    cons:['Still vanishes for |x|>2','Slow exp compute'],                      use:'RNNs hidden state, prefer over sigmoid in hidden.' },
  leaky_relu: { pros:['No dying ReLU (leak=0.01)','Cheap compute','Gradient everywhere'],     cons:['0.01 leak is a hyperparameter','Not zero-centered'],                 use:'Drop-in replacement for ReLU if dying neurons observed.' },
  elu:        { pros:['Smooth negative region','Zero mean activations','Faster convergence'],  cons:['Exp for x<0','α is hyperparameter','Slightly slower'],               use:'Deeper networks where you want near-zero mean.' },
  swish:      { pros:['Smooth, non-monotonic','Self-gated','Outperforms ReLU on deep nets'],   cons:['More expensive than ReLU','Unbounded above'],                        use:'Transformers, deep vision nets (Google Brain recommends).' },
  gelu:       { pros:['Smooth everywhere','Best empirical results in transformers','Stochastic interpretation'], cons:['Most expensive','Complex formula'],              use:'BERT, GPT, all modern transformers use this.' },
  softplus:   { pros:['Smooth ReLU approximation','Always positive gradient'],                cons:['Slower than ReLU','Less sparse'],                                    use:'When you need smooth ReLU without dying neurons.' },
};

const xRange = Array.from({length:101},(_,i)=>-5+i*0.1);

const buildData = () => xRange.map(x=>{
  const row={x:+x.toFixed(2)};
  Object.entries(ACTS).forEach(([k,v])=>{ row[k]=+v.fn(x).toFixed(5); row[`d_${k}`]=+v.d(x).toFixed(5); });
  return row;
});
const ALL_DATA = buildData();

// ═══════════════════════════════════════════════════════════════
// TAB: EXPLORER — deep dive on one activation
// ═══════════════════════════════════════════════════════════════
function TabExplorer() {
  const [sel, setSel] = useState('relu');
  const act = ACTS[sel];
  const meta = ACT_META[sel];

  // Show where gradient is near zero
  const deadZones = ALL_DATA.filter(d=>Math.abs(d[`d_${sel}`])<0.05);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
      {/* Selector */}
      <div style={{display:'flex',gap:'4px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',flexWrap:'wrap'}}>
        {Object.entries(ACTS).map(([k,v])=>(
          <button key={k} onClick={()=>setSel(k)} style={{
            padding:'6px 13px',border:`1px solid ${sel===k?v.color:'transparent'}`,
            background:sel===k?v.color+'20':'transparent',color:sel===k?v.color:'var(--t3)',
            fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:sel===k?700:400,
          }}>{k.toUpperCase()}</button>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
        {/* f(x) */}
        <Panel title={`${sel.toUpperCase()} — f(x)`} accent={act.color} badge={act.formula}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={ALL_DATA}>
              <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
              <XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}} domain={[-5,5]}/>
              <YAxis stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}} domain={[-2,4]}/>
              <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:10,fontFamily:'JetBrains Mono'}}/>
              <ReferenceLine x={0} stroke="var(--b1)"/><ReferenceLine y={0} stroke="var(--b1)"/>
              <Line type="monotone" dataKey={sel} stroke={act.color} dot={false} strokeWidth={2.5}/>
            </LineChart>
          </ResponsiveContainer>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'6px',marginTop:'8px'}}>
            {[
              {label:'Output Range', value:act.range, color:act.color},
              {label:'Zero-Centered', value:act.zeroCentered?'Yes':'No', color:act.zeroCentered?'var(--green)':'var(--red)'},
              {label:'Dead Zones', value:`${deadZones.length} of 101 pts`, color:deadZones.length>30?'var(--red)':'var(--green)'},
            ].map(m=>(
              <div key={m.label} style={{padding:'7px',background:'var(--deep)',borderRadius:'2px',textAlign:'center'}}>
                <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'2px'}}>{m.label}</div>
                <div style={{fontSize:'11px',color:m.color,fontWeight:700}}>{m.value}</div>
              </div>
            ))}
          </div>
        </Panel>

        {/* f'(x) */}
        <Panel title={`${sel.toUpperCase()} — DERIVATIVE f'(x)`} accent="var(--gold)">
          <InfoBox color="var(--gold)" icon="◈">
            This is what backprop multiplies at each neuron. Zero = gradient stops. Large = gradient explodes.
          </InfoBox>
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={ALL_DATA}>
              <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
              <XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
              <YAxis stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}} domain={[-0.05,1.3]}/>
              <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:10,fontFamily:'JetBrains Mono'}}/>
              <ReferenceLine y={0} stroke="var(--b1)"/>
              <ReferenceLine y={0.05} stroke="var(--red)" strokeDasharray="3 3" label={{value:'saturation zone',fill:'var(--red)',fontSize:8,position:'right'}}/>
              <Line type="monotone" dataKey={`d_${sel}`} stroke="var(--gold)" dot={false} strokeWidth={2.5}/>
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Pros/cons + use case */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'12px'}}>
        <Panel title="ADVANTAGES" accent="var(--green)">
          {meta.pros.map((p,i)=>(
            <div key={i} style={{display:'flex',gap:'7px',marginBottom:'7px',fontSize:'11px',color:'var(--t2)'}}>
              <span style={{color:'var(--green)',flexShrink:0,marginTop:'1px'}}>✓</span>{p}
            </div>
          ))}
        </Panel>
        <Panel title="DISADVANTAGES" accent="var(--red)">
          {meta.cons.map((c,i)=>(
            <div key={i} style={{display:'flex',gap:'7px',marginBottom:'7px',fontSize:'11px',color:'var(--t2)'}}>
              <span style={{color:'var(--red)',flexShrink:0,marginTop:'1px'}}>✗</span>{c}
            </div>
          ))}
        </Panel>
        <Panel title="WHEN TO USE" accent={act.color}>
          <div style={{fontSize:'11px',color:'var(--t2)',lineHeight:1.65,marginBottom:'10px'}}>{meta.use}</div>
          <div style={{padding:'8px',background:'var(--deep)',borderRadius:'2px'}}>
            <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>FORMULA</div>
            <code style={{fontSize:'12px',color:act.color}}>{act.formula}</code>
          </div>
        </Panel>
      </div>

      {/* Dead zone heatmap */}
      <Panel title={`GRADIENT MAGNITUDE ACROSS INPUT RANGE — ${sel.toUpperCase()}`} accent="var(--violet)">
        <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'8px'}}>
          Each cell = |f'(x)| at that x value. Dark = near zero = vanishing gradient. Bright = healthy gradient flow.
        </div>
        <div style={{display:'flex',gap:'1px',flexWrap:'nowrap',overflowX:'auto'}}>
          {ALL_DATA.filter((_,i)=>i%2===0).map((d,i)=>{
            const gv=Math.abs(d[`d_${sel}`]);
            const norm=Math.min(1,gv);
            return (
              <div key={i} title={`x=${d.x} → f'=${d[`d_${sel}`].toFixed(4)}`} style={{
                width:'14px',height:'40px',flexShrink:0,borderRadius:'1px',
                background:`${act.color}${Math.round(10+norm*230).toString(16).padStart(2,'0')}`,
                border:`1px solid ${gv<0.05?'var(--red)44':'transparent'}`,
              }}/>
            );
          })}
        </div>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:'8px',color:'var(--t3)',marginTop:'4px'}}>
          <span>x = -5</span><span>x = 0</span><span>x = +5</span>
        </div>
        {deadZones.length>0 && (
          <InfoBox color="var(--red)" icon="⚠" style={{marginTop:'8px'}}>
            {deadZones.length} input values ({((deadZones.length/ALL_DATA.length)*100).toFixed(0)}% of range) have |f'(x)| &lt; 0.05 — near-zero gradients. Weights won't update for inputs landing here.
          </InfoBox>
        )}
      </Panel>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: COMPARE — all activations overlaid
// ═══════════════════════════════════════════════════════════════
function TabCompare() {
  const [visible, setVisible] = useState(()=>Object.fromEntries(Object.keys(ACTS).map(k=>[k,true])));
  const toggle = k => setVisible(v=>({...v,[k]:!v[k]}));

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
      {/* Toggle buttons */}
      <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
        {Object.entries(ACTS).map(([k,v])=>(
          <button key={k} onClick={()=>toggle(k)} style={{
            padding:'5px 12px',border:`1px solid ${visible[k]?v.color:'var(--b1)'}`,
            background:visible[k]?v.color+'18':'transparent',color:visible[k]?v.color:'var(--t3)',
            fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',
            fontWeight:visible[k]?700:400,transition:'all .12s',
          }}>{visible[k]?'●':'+'}  {k}</button>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
        <Panel title="f(x) — ALL ACTIVATIONS" accent={AC}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={ALL_DATA}>
              <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
              <XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
              <YAxis stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}} domain={[-2,4]}/>
              <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:9,fontFamily:'JetBrains Mono'}}/>
              <ReferenceLine x={0} stroke="var(--b1)"/><ReferenceLine y={0} stroke="var(--b1)"/>
              {Object.entries(ACTS).filter(([k])=>visible[k]).map(([k,v])=>(
                <Line key={k} type="monotone" dataKey={k} stroke={v.color} dot={false} strokeWidth={2} name={k}/>
              ))}
              <Legend/>
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="f'(x) — DERIVATIVE COMPARISON" accent="var(--gold)">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={ALL_DATA}>
              <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
              <XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
              <YAxis stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}} domain={[-0.05,1.3]}/>
              <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:9,fontFamily:'JetBrains Mono'}}/>
              <ReferenceLine y={0} stroke="var(--b1)"/>
              {Object.entries(ACTS).filter(([k])=>visible[k]).map(([k,v])=>(
                <Line key={k} type="monotone" dataKey={`d_${k}`} stroke={v.color} dot={false} strokeWidth={2} name={k}/>
              ))}
              <Legend/>
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      {/* Difference chart: swish vs relu */}
      <Panel title="KEY INSIGHT — SWISH vs RELU vs GELU (MODERN ACTIVATIONS)" accent="var(--violet)">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px',alignItems:'start'}}>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={ALL_DATA}>
              <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
              <XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
              <YAxis stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}} domain={[-1,4]}/>
              <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:9,fontFamily:'JetBrains Mono'}}/>
              <ReferenceLine x={0} stroke="var(--b1)"/><ReferenceLine y={0} stroke="var(--b1)"/>
              <Line type="monotone" dataKey="relu"  stroke={P[0]} dot={false} strokeWidth={2} name="ReLU"/>
              <Line type="monotone" dataKey="swish" stroke={P[5]} dot={false} strokeWidth={2} name="Swish"/>
              <Line type="monotone" dataKey="gelu"  stroke={P[6]} dot={false} strokeWidth={2} name="GELU"/>
              <Legend/>
            </LineChart>
          </ResponsiveContainer>
          <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
            {[
              {name:'ReLU',  point:'Hard zero at x<0. 50% of neurons dead per forward pass — by design. Sparse = efficient.',  color:P[0]},
              {name:'Swish', point:'Self-gated: x·σ(x). Slight negative region near x=-1. Smooth. 10-20% better than ReLU on deep nets empirically.', color:P[5]},
              {name:'GELU',  point:'Smooth stochastic version of ReLU. Drop token interpretation. Used in every modern transformer: BERT, GPT, T5.', color:P[6]},
            ].map(r=>(
              <div key={r.name} style={{padding:'10px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${r.color}`}}>
                <div style={{fontSize:'10px',color:r.color,fontWeight:700,marginBottom:'3px'}}>{r.name}</div>
                <div style={{fontSize:'10px',color:'var(--t2)',lineHeight:1.55}}>{r.point}</div>
              </div>
            ))}
          </div>
        </div>
      </Panel>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: SATURATION
// ═══════════════════════════════════════════════════════════════
function TabSaturation() {
  const thresholds = [0.01, 0.05, 0.1, 0.2];
  const [thresh, setThresh] = useState(0.05);

  const satData = Object.entries(ACTS).map(([k,v])=>{
    const saturated = ALL_DATA.filter(d=>Math.abs(d[`d_${k}`])<thresh).length;
    const pct = +(saturated/ALL_DATA.length*100).toFixed(1);
    return {name:k, pct, saturated, total:ALL_DATA.length, color:v.color};
  }).sort((a,b)=>a.pct-b.pct);

  const layerEffect = [1,2,3,4,5,6,8,10,12].map(L=>({
    L,
    sigmoid: +(0.25**L).toFixed(8),  // max gradient per layer for sigmoid
    tanh:    +(0.42**L).toFixed(8),   // max gradient per layer for tanh
    relu:    +(1.0**L).toFixed(4),    // relu keeps gradient intact
  }));

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
      {/* Threshold selector */}
      <Panel title="SATURATION THRESHOLD ANALYSIS" accent="var(--red)">
        <div style={{display:'flex',gap:'6px',marginBottom:'12px',alignItems:'center'}}>
          <span style={{fontSize:'10px',color:'var(--t2)'}}>|f'(x)| &lt; </span>
          {thresholds.map(t=>(
            <button key={t} onClick={()=>setThresh(t)} style={{
              padding:'4px 10px',border:`1px solid ${thresh===t?'var(--red)':'var(--b1)'}`,
              background:thresh===t?'var(--red)18':'transparent',color:thresh===t?'var(--red)':'var(--t3)',
              fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:thresh===t?700:400,
            }}>{t}</button>
          ))}
          <span style={{fontSize:'10px',color:'var(--t3)',marginLeft:'8px'}}>= "saturated" neuron</span>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={satData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
            <XAxis type="number" domain={[0,100]} stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t3)'}} tickFormatter={v=>`${v}%`}/>
            <YAxis type="category" dataKey="name" stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t2)'}} width={72}/>
            <Tooltip formatter={v=>`${v}%`} contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:10,fontFamily:'JetBrains Mono'}}/>
            <ReferenceLine x={50} stroke="var(--red)" strokeDasharray="4 2"/>
            <Bar dataKey="pct" radius={[0,3,3,0]}>
              {satData.map((d,i)=><Cell key={i} fill={d.color}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <InfoBox color="var(--red)" icon="⚠">
          Red line at 50% = danger zone. Sigmoid/tanh saturate on both tails. ReLU saturates only left half (x&lt;0) — but those neurons are intentionally dead (sparse).
        </InfoBox>
      </Panel>

      {/* Vanishing gradient across depth */}
      <Panel title="VANISHING GRADIENT — GRADIENT MAGNITUDE VS NETWORK DEPTH" accent="var(--orange)">
        <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'8px'}}>
          If gradient per layer = 0.25 (sigmoid max), after L layers: total gradient = 0.25ᴸ → 0 exponentially.
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={layerEffect}>
            <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
            <XAxis dataKey="L" stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'depth (layers)',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
            <YAxis stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t3)'}} tickFormatter={v=>v.toFixed(4)}/>
            <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:9,fontFamily:'JetBrains Mono'}} formatter={v=>v.toFixed(8)}/>
            <ReferenceLine y={0.001} stroke="var(--red)" strokeDasharray="3 3" label={{value:'≈0 (dead)',fill:'var(--red)',fontSize:8}}/>
            <Line type="monotone" dataKey="sigmoid" stroke={P[2]} dot={{r:3}} strokeWidth={2} name="Sigmoid (0.25ᴸ)"/>
            <Line type="monotone" dataKey="tanh"    stroke={P[1]} dot={{r:3}} strokeWidth={2} name="Tanh (0.42ᴸ)"/>
            <Line type="monotone" dataKey="relu"    stroke={P[0]} dot={{r:3}} strokeWidth={2} name="ReLU (1.0ᴸ — stable)"/>
            <Legend/>
          </LineChart>
        </ResponsiveContainer>
        <InfoBox color="var(--orange)" icon="◈">
          At depth=6: sigmoid gradient ≈ 0.00024. Weights in layer 1 get 4000× smaller updates than layer 6. This is why deep sigmoid networks failed before ReLU + residuals.
        </InfoBox>
      </Panel>

      {/* Solutions */}
      <Panel title="SOLUTIONS TO VANISHING GRADIENTS" accent="var(--green)">
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
          {[
            {fix:'Use ReLU', detail:'Gradient = 1 for x>0. No saturation on positive side. Simple, effective.', color:P[0]},
            {fix:'Residual Connections', detail:'x → x + f(x). Gradient highway bypasses layers. ResNet enables 1000+ layer training.', color:P[1]},
            {fix:'Batch Normalization', detail:'Normalize activations per batch. Keeps them in non-saturating range. Huge improvement.', color:P[2]},
            {fix:'Better Init', detail:'Xavier/He init keeps activations in healthy range from start. Prevents saturation before training begins.', color:P[3]},
          ].map(s=>(
            <div key={s.fix} style={{padding:'11px',background:'var(--deep)',borderRadius:'2px',borderTop:`2px solid ${s.color}`}}>
              <div style={{fontSize:'10px',color:s.color,fontWeight:700,marginBottom:'5px'}}>{s.fix}</div>
              <div style={{fontSize:'10px',color:'var(--t2)',lineHeight:1.55}}>{s.detail}</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: DERIVATIVES — interactive gradient flow visualizer
// ═══════════════════════════════════════════════════════════════
function TabDerivatives() {
  const [x, setX] = useState(0);
  const [actKey, setActKey] = useState('relu');
  const act = ACTS[actKey];

  const fval  = act.fn(x);
  const dval  = act.d(x);
  const range = Array.from({length:61},(_,i)=>-3+i*0.1);

  // Show what gradient looks like for a chain of 5 layers
  const chainGrads = Array.from({length:6},(_,i)=>({
    layer:`L${i+1}`,
    gradient:+Math.pow(Math.abs(dval),i+1).toFixed(6),
    cumulative:+Math.pow(Math.abs(dval),i+1).toFixed(6),
  }));

  return (
    <div style={{display:'grid',gridTemplateColumns:'320px 1fr',gap:'14px'}}>
      {/* Controls */}
      <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
        <Panel title="INTERACTIVE GRADIENT PROBE" accent="var(--gold)">
          <div style={{marginBottom:'10px'}}>
            <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'6px'}}>ACTIVATION</div>
            <div style={{display:'flex',gap:'4px',flexWrap:'wrap'}}>
              {Object.entries(ACTS).map(([k,v])=>(
                <button key={k} onClick={()=>setActKey(k)} style={{
                  padding:'4px 9px',border:`1px solid ${actKey===k?v.color:'var(--b1)'}`,
                  background:actKey===k?v.color+'20':'transparent',color:actKey===k?v.color:'var(--t3)',
                  fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',
                }}>{k}</button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:'10px'}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'5px'}}>
              <span style={{fontSize:'10px',color:'var(--t2)'}}>Input x</span>
              <span style={{fontSize:'12px',color:'var(--t1)',fontWeight:700}}>{x.toFixed(2)}</span>
            </div>
            <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px',marginBottom:'2px'}}>
              <div style={{position:'absolute',left:0,height:'100%',borderRadius:'2px',background:`linear-gradient(90deg,var(--gold)44,var(--gold))`,width:`${((x+3)/6)*100}%`}}/>
              <input type="range" min={-3} max={3} step={0.05} value={x} onChange={e=>setX(parseFloat(e.target.value))}
                style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:'8px',color:'var(--t3)'}}><span>-3</span><span>0</span><span>+3</span></div>
          </div>

          {/* Computed values */}
          <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
            {[
              {label:'Input x',       val:x.toFixed(4),   color:'var(--t1)'},
              {label:'f(x)',          val:fval.toFixed(6), color:act.color},
              {label:"f'(x) gradient", val:dval.toFixed(6), color:Math.abs(dval)<0.05?'var(--red)':Math.abs(dval)<0.3?'var(--gold)':'var(--green)'},
              {label:'Saturated?',    val:Math.abs(dval)<0.05?'YES — gradient ≈ 0':'NO — gradient healthy', color:Math.abs(dval)<0.05?'var(--red)':'var(--green)'},
            ].map(r=>(
              <div key={r.label} style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',background:'var(--deep)',borderRadius:'2px'}}>
                <span style={{fontSize:'10px',color:'var(--t3)'}}>{r.label}</span>
                <span style={{fontSize:'10px',color:r.color,fontWeight:700}}>{r.val}</span>
              </div>
            ))}
          </div>
        </Panel>

        {/* Chain effect */}
        <Panel title="CHAIN RULE ACROSS 6 LAYERS" accent="var(--orange)">
          <div style={{fontSize:'9px',color:'var(--t2)',marginBottom:'8px'}}>
            If f'(x)={dval.toFixed(3)} at this input, gradient after L layers = f'(x)ᴸ
          </div>
          {chainGrads.map((g,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px'}}>
              <span style={{fontSize:'9px',color:'var(--t3)',width:'24px'}}>{g.layer}</span>
              <div style={{flex:1,height:'14px',background:'var(--b0)',borderRadius:'2px',overflow:'hidden'}}>
                <div style={{
                  height:'100%',
                  width:`${Math.min(100,g.gradient*100)}%`,
                  background:g.gradient<0.001?'var(--red)':g.gradient<0.1?'var(--gold)':'var(--green)',
                  borderRadius:'2px',
                }}/>
              </div>
              <span style={{fontSize:'9px',width:'72px',textAlign:'right',color:g.gradient<0.001?'var(--red)':g.gradient<0.1?'var(--gold)':'var(--green)',fontWeight:700}}>
                {g.gradient.toFixed(6)}
              </span>
            </div>
          ))}
          {Math.abs(dval)<0.3 && (
            <InfoBox color="var(--red)" icon="⚠" style={{marginTop:'8px'}}>
              At x={x.toFixed(2)}, f'={dval.toFixed(3)} → after 6 layers: {Math.pow(Math.abs(dval),6).toFixed(8)} — effectively zero!
            </InfoBox>
          )}
        </Panel>
      </div>

      {/* Right: gradient map */}
      <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
        <Panel title="GRADIENT LANDSCAPE — FULL INPUT RANGE" accent={act.color}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={range.map(xi=>({x:+xi.toFixed(2),fn:+act.fn(xi).toFixed(5),deriv:+act.d(xi).toFixed(5)}))}>
              <CartesianGrid stroke="var(--b0)" strokeDasharray="3 3"/>
              <XAxis dataKey="x" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
              <YAxis stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}} domain={[-1.5,3]}/>
              <Tooltip contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:9,fontFamily:'JetBrains Mono'}}/>
              <ReferenceLine x={0} stroke="var(--b1)"/><ReferenceLine y={0} stroke="var(--b1)"/>
              <ReferenceLine x={x} stroke="var(--gold)" strokeWidth={2} label={{value:`x=${x.toFixed(1)}`,fill:'var(--gold)',fontSize:9}}/>
              <Line type="monotone" dataKey="fn"    stroke={act.color} dot={false} strokeWidth={2.5} name="f(x)"/>
              <Line type="monotone" dataKey="deriv" stroke="var(--gold)" dot={false} strokeWidth={2} strokeDasharray="5 2" name="f'(x)"/>
              <Legend/>
            </LineChart>
          </ResponsiveContainer>
          <InfoBox color="var(--t3)" icon="◈">
            Gold dashed = gradient. Move the x slider to probe different points. Watch how gradient drops near saturation zones.
          </InfoBox>
        </Panel>

        {/* Gradient for all activations at current x */}
        <Panel title={`GRADIENT COMPARISON AT x = ${x.toFixed(2)}`} accent="var(--violet)">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={Object.entries(ACTS).map(([k,v])=>({name:k,gradient:+Math.abs(v.d(x)).toFixed(5),color:v.color}))}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
              <XAxis dataKey="name" stroke="var(--t3)" tick={{fontSize:8,fill:'var(--t3)'}}/>
              <YAxis stroke="var(--t3)" tick={{fontSize:9,fill:'var(--t3)'}}/>
              <Tooltip formatter={v=>`|f'(x)| = ${v}`} contentStyle={{background:'var(--raised)',border:'1px solid var(--b1)',fontSize:10,fontFamily:'JetBrains Mono'}}/>
              <ReferenceLine y={0.05} stroke="var(--red)" strokeDasharray="4 2" label={{value:'danger: 0.05',fill:'var(--red)',fontSize:8}}/>
              <Bar dataKey="gradient" radius={[3,3,0,0]}>
                {Object.entries(ACTS).map(([k,v],i)=><Cell key={i} fill={Math.abs(v.d(x))<0.05?'var(--red)':v.color}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TAB: CHEATSHEET
// ═══════════════════════════════════════════════════════════════
function TabCheatsheet() {
  const rows = [
    {act:'ReLU',        formula:'max(0,x)',        range:'[0,∞)',  zc:false, vanish:'Left half only', use:'Default hidden layers',          modern:true,  color:P[0]},
    {act:'Leaky ReLU',  formula:'max(0.01x, x)',   range:'(-∞,∞)', zc:false, vanish:'None',          use:'If dying ReLU observed',          modern:false, color:P[3]},
    {act:'ELU',         formula:'x≥0?x:α(eˣ−1)',   range:'(-α,∞)', zc:false, vanish:'None',          use:'Deeper nets, near-zero mean',     modern:false, color:P[4]},
    {act:'GELU',        formula:'0.5x(1+tanh(...))',range:'(-∞,∞)', zc:false, vanish:'Tiny neg',      use:'ALL modern transformers',         modern:true,  color:P[6]},
    {act:'Swish',       formula:'x·σ(x)',          range:'(-∞,∞)', zc:false, vanish:'Tiny neg',      use:'Deep vision/NLP nets',            modern:true,  color:P[5]},
    {act:'Sigmoid',     formula:'1/(1+e⁻ˣ)',       range:'(0,1)',  zc:false, vanish:'Both tails',    use:'ONLY output (binary class)',      modern:false, color:P[2]},
    {act:'Tanh',        formula:'(eˣ−e⁻ˣ)/(eˣ+e⁻ˣ)', range:'(-1,1)', zc:true, vanish:'Both tails', use:'RNN hidden state, prefer over sigmoid', modern:false, color:P[1]},
    {act:'Softmax',     formula:'eˣᵢ/Σeˣʲ',       range:'(0,1)',  zc:false, vanish:'—',             use:'ONLY output (multi-class)',       modern:true,  color:'var(--t2)'},
    {act:'Linear',      formula:'x',               range:'(-∞,∞)', zc:true, vanish:'None',          use:'ONLY output (regression)',        modern:true,  color:'var(--t3)'},
  ];

  const decisionTree = [
    {q:'Output layer for binary classification?', a:'→ Sigmoid'},
    {q:'Output layer for multi-class classification?', a:'→ Softmax'},
    {q:'Output layer for regression?', a:'→ Linear (no activation)'},
    {q:'Hidden layers — default choice?', a:'→ ReLU (fast, works)'},
    {q:'Hidden layers — dying neurons observed?', a:'→ Leaky ReLU or ELU'},
    {q:'Building a Transformer / modern LLM?', a:'→ GELU (standard)'},
    {q:'Building a CNN image model (Google-style)?', a:'→ Swish'},
    {q:'RNN hidden state?', a:'→ Tanh (bounded, zero-centered)'},
  ];

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
      {/* Full table */}
      <Panel title="ACTIVATION FUNCTION QUICK REFERENCE" accent={AC}>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'10px',fontFamily:'JetBrains Mono'}}>
            <thead>
              <tr style={{background:'var(--deep)'}}>
                {['Function','Formula','Range','Zero-Centered','Vanishing','Use When','Modern?'].map(h=>(
                  <th key={h} style={{padding:'7px 10px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b1)',letterSpacing:'.08em',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r,i)=>(
                <tr key={r.act} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.06)'}}>
                  <td style={{padding:'6px 10px',color:r.color,fontWeight:700}}>{r.act}</td>
                  <td style={{padding:'6px 10px',color:'var(--t2)',fontFamily:'monospace',fontSize:'10px'}}>{r.formula}</td>
                  <td style={{padding:'6px 10px',color:'var(--t3)'}}>{r.range}</td>
                  <td style={{padding:'6px 10px',color:r.zc?'var(--green)':'var(--red)'}}>{r.zc?'✓ Yes':'✗ No'}</td>
                  <td style={{padding:'6px 10px',color:r.vanish.includes('Both')?'var(--red)':r.vanish==='None'?'var(--green)':'var(--gold)'}}>{r.vanish}</td>
                  <td style={{padding:'6px 10px',color:'var(--t2)'}}>{r.use}</td>
                  <td style={{padding:'6px 10px',color:r.modern?'var(--green)':'var(--t3)'}}>{r.modern?'✓':''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* Decision tree */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
        <Panel title="HOW TO PICK AN ACTIVATION — DECISION TREE" accent="var(--cyan)">
          {decisionTree.map((d,i)=>(
            <div key={i} style={{display:'flex',gap:'10px',alignItems:'flex-start',marginBottom:'10px',padding:'8px',background:'var(--deep)',borderRadius:'2px'}}>
              <span style={{color:'var(--cyan)',fontSize:'10px',fontWeight:700,flexShrink:0}}>{i+1}.</span>
              <div>
                <div style={{fontSize:'10px',color:'var(--t2)',marginBottom:'3px'}}>{d.q}</div>
                <div style={{fontSize:'11px',color:'var(--cyan)',fontWeight:700}}>{d.a}</div>
              </div>
            </div>
          ))}
        </Panel>

        <Panel title="COMMON MISTAKES" accent="var(--red)">
          {[
            {mistake:'Using sigmoid/tanh in hidden layers', fix:'Use ReLU or GELU instead. Sigmoid/tanh are for output layers only in modern networks.', color:'var(--red)'},
            {mistake:'Using ReLU on output layer for regression', fix:'Output layer for regression needs Linear (identity). ReLU would clip negatives.', color:'var(--red)'},
            {mistake:'Using linear activation in hidden layers', fix:'Without non-linearity, the whole network collapses to a single linear transform regardless of depth.', color:'var(--gold)'},
            {mistake:'Ignoring dying ReLU problem', fix:'If your loss is stuck, check if neurons are outputting all zeros. Switch to Leaky ReLU or lower learning rate.', color:'var(--gold)'},
            {mistake:'Using softmax on hidden layers', fix:'Softmax is a normalizing operation for probabilities. It forces competition between neurons — catastrophic in hidden layers.', color:'var(--orange)'},
          ].map((m,i)=>(
            <div key={i} style={{marginBottom:'10px',padding:'10px',background:'var(--deep)',borderRadius:'2px',borderLeft:`2px solid ${m.color}`}}>
              <div style={{fontSize:'10px',color:m.color,fontWeight:700,marginBottom:'4px'}}>✗ {m.mistake}</div>
              <div style={{fontSize:'10px',color:'var(--t2)',lineHeight:1.55}}>→ {m.fix}</div>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════
export default function ActivationsLab({ mode }) {
  const [tab,  setTab]  = useState('explorer');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Activations Lab — explore activation functions in depth.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-60),{ts:timestamp(),msg,t}]);

  const TAB_META = {
    explorer:    {label:'EXPLORER',     color:'var(--gold)'},
    compare:     {label:'COMPARE',      color:'var(--cyan)'},
    saturation:  {label:'SATURATION',   color:'var(--red)'},
    derivatives: {label:'DERIVATIVES',  color:'var(--violet)'},
    cheatsheet:  {label:'CHEATSHEET',   color:'var(--green)'},
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Tabs */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:'6px 14px',border:`1px solid ${tab===t?TAB_META[t].color:'transparent'}`,
            background:tab===t?TAB_META[t].color+'20':'transparent',
            color:tab===t?TAB_META[t].color:'var(--t3)',
            fontSize:'10px',cursor:'pointer',borderRadius:'2px',
            fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400,
          }}>{TAB_META[t].label}</button>
        ))}
      </div>

      <div>
        {tab==='explorer'    && <TabExplorer/>}
        {tab==='compare'     && <TabCompare/>}
        {tab==='saturation'  && <TabSaturation/>}
        {tab==='derivatives' && <TabDerivatives/>}
        {tab==='cheatsheet'  && <TabCheatsheet/>}
      </div>

      <LogPanel logs={logs} height={70}/>
    </div>
  );
}
